import _ from "lodash";
import { useState, useMemo, forwardRef, type ForwardedRef } from "react";
import { type SampleDocument } from "@/utils";
import Selector from "@/components/ui/design-system/Selector";
import { SearchInput } from "@/components/ui/design-system/SearchInput";
import { LoadingDots } from "@/components/ui/icons";

export type SampleDocumentSelectorProps = {
  label?: string;
  documentsToExtract?: SampleDocument[];
  documentDisplayMaxCount?: number;
  value: string;
  error?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
};

export const SampleDocumentSelector = forwardRef(
  (
    {
      label = "What types of documents do you want to extract?",
      documentsToExtract,
      documentDisplayMaxCount = 9,
      value,
      error,
      onChange,
      disabled,
      loading = false,
    }: SampleDocumentSelectorProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const [search, setSearch] = useState("");
    const [showMore, setShowMore] = useState(false);

    const filteredDocs = useMemo(() => {
      return documentsToExtract?.filter((elem) => {
        const { display_name } = elem;
        return display_name.toLowerCase().includes(search.toLowerCase());
      });
    }, [documentsToExtract, search]);

    const documentsToShow = useMemo(() => {
      return _.take(
        filteredDocs,
        showMore ? filteredDocs?.length : documentDisplayMaxCount
      );
    }, [documentDisplayMaxCount, filteredDocs, showMore]);

    if (loading)
      return (
        <div className="flex flex-col h-full min-h-[85px] items-center justify-center space-y-ds-2">
          <span>Loading sample documents...</span>
          <LoadingDots className="h-ds-2 text-gray-300" />
        </div>
      );

    return (
      <div className={"flex flex-col gap-ds-2"} ref={ref}>
        <span className={"font-semibold"}>{label}</span>
        <div className={"flex flex-col gap-ds-2"}>
          {(documentsToExtract?.length || 0) > documentDisplayMaxCount && (
            <SearchInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowMore(false);
              }}
              onClickClear={() => {
                setSearch("");
              }}
              className={"mb-ds-1"}
              placeholder={"Search"}
            />
          )}
          <div className={"grid grid-cols-3 gap-ds-1"}>
            {documentsToShow.map((elem) => (
              <Selector
                icon={elem.icon}
                onClick={() => {
                  if (disabled) return;
                  onChange?.(elem.path);
                }}
                selected={value === elem.path}
                key={elem.path}
                disabled={disabled}
              >
                {elem.display_name}
              </Selector>
            ))}
          </div>
          {(filteredDocs?.length || 0) > documentDisplayMaxCount && (
            <span
              className={"cursor-pointer"}
              onClick={() => setShowMore(!showMore)}
            >
              {!showMore ? `Show more` : `Show less`}
            </span>
          )}
          <span className={"text-error-dark"}>{error}</span>
        </div>
      </div>
    );
  }
);

SampleDocumentSelector.displayName = "SampleDocumentSelector";
